import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../lib/analytics";

// Disable native scroll restoration — we handle it ourselves
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// State flags
let _isBackNav = false;
let _currentPath = typeof window !== "undefined" ? window.location.pathname : "";
let _restoringScroll = false;
let _transitioning = false;

if (typeof window !== "undefined") {
  // Detect back/forward navigation
  window.addEventListener("popstate", () => { _isBackNav = true; });

  // Capture internal link clicks BEFORE React processes them.
  // Save scroll position immediately when user clicks any internal link.
  document.addEventListener("click", (e) => {
    const link = e.target.closest("a[href]");
    if (!link) return;
    const href = link.getAttribute("href");
    // Only internal links (not external, not hash-only)
    if (!href || href.startsWith("http") || href.startsWith("//") || href === "#") return;
    // Save scroll for current path before navigation starts
    if (_currentPath) {
      sessionStorage.setItem(`scroll_${_currentPath}`, String(window.scrollY));
      _transitioning = true;
    }
  }, true); // capture phase — runs before React's event handlers

  // Also intercept pushState as backup
  const _origPush = history.pushState.bind(history);
  history.pushState = function(...args) {
    if (_currentPath && !_isBackNav && !_transitioning) {
      sessionStorage.setItem(`scroll_${_currentPath}`, String(window.scrollY));
      _transitioning = true;
    }
    return _origPush(...args);
  };
}

export function getSavedScroll(key) {
  const v = sessionStorage.getItem(`scroll_${key}`);
  return v ? parseInt(v, 10) : 0;
}

export function clearSavedScroll(key) {
  sessionStorage.removeItem(`scroll_${key}`);
}

export function isBackNavigation() {
  return _isBackNav;
}

export function consumeBackNavigation() {
  const was = _isBackNav;
  _isBackNav = false;
  return was;
}

export function restoreScrollPosition(key) {
  const target = getSavedScroll(key);
  if (!target) return;
  _restoringScroll = true;
  let attempts = 0;
  const tryScroll = () => {
    if (attempts >= 50) { _restoringScroll = false; return; }
    attempts++;
    if (document.documentElement.scrollHeight > target + 200 || attempts > 30) {
      window.scrollTo({ top: target, left: 0, behavior: "instant" });
      setTimeout(() => {
        window.scrollTo({ top: target, left: 0, behavior: "instant" });
        setTimeout(() => { _restoringScroll = false; }, 300);
      }, 200);
    } else {
      requestAnimationFrame(tryScroll);
    }
  };
  requestAnimationFrame(tryScroll);
}

// Keep these for backward compatibility with any code that imports them
export function saveScrollPosition(key) {
  sessionStorage.setItem(`scroll_${key}`, String(window.scrollY));
}

export default function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);

  useEffect(() => {
    // Transition complete — re-enable normal operation
    _transitioning = false;

    // Update tracked path
    _currentPath = pathname;
    prevPath.current = pathname;

    // Track page view
    trackPageView(pathname, document.title);

    // If back navigation, don't scroll to top — target page handles restoration
    if (_isBackNav) {
      return;
    }

    // Forward navigation: scroll to top
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [pathname]);

  return null;
}
